import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Settings -> Contacts, calea noua.
 *
 * Perechile vechi, din `application/controllers/settings/Contacts.php`:
 *
 *   index (bara si filele)      -> GET  /settings/contacts
 *   lista contactelor firmei    -> GET  /settings/contacts/list
 *   editContact/{id}            -> GET  /settings/contacts/{id}
 *   add_contact + update_contact-> POST /settings/contacts
 *   events/{id}, getContactEvents/{id}
 *                               -> GET  /settings/contacts/{id}/events
 *   subscriptions/{id}          -> GET  /settings/contacts/{id}/subscriptions
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * Lista veche a acestui ecran (`contacts/index.php` + `settings/contacts.js`)
 * cerea `settings/contacts/contactsData`, o metoda care nu exista in
 * controller - ecranul era rupt de cand a fost scris. Lista de aici e cea VIE,
 * aceeasi pe care o vede omul in "Device Owners".
 *
 * O salvare refuzata NU vine ca `status: ERROR` intr-un 200, ca in portal: vine
 * ca 400 cu campul si mesajul serverului, deci ajunge la ecran ca `ApiError`.
 * Un contact al altei firme vine ca 404, nu 403 - un 403 ar confirma ca ID-ul
 * ghicit exista.
 */

/** O fila din bara. `group` e capul de sectiune, ca la Settings -> General. */
export type ContactsTab = {
  id: string
  label: string
  icon: string
  href: string
  right: string
  group: string
}

/** O fila a panoului unui contact - cele doua din `contacts/tab_list.php`. */
export type ContactPanelTab = {
  id: 'subscriptions' | 'events'
  label: string
  right: string
}

export type ContactsShell = {
  pageTitle: string
  moduleUrl: string
  /** DEJA filtrate pe server. */
  tabs: ContactsTab[]
  panelTabs: ContactPanelTab[]
  /** Firma pentru care se lucreaza; contactul nou intra in ea. */
  companyId: number
}

/** Raspunsul unei liste, in forma pe care o astepta DataTables. */
export type TablePage<T> = {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: T[]
}

/** Un rand din lista de contacte - coloanele din `OwnersSupportModel`. */
export type ContactRow = {
  id: number
  firstname: string
  lastname: string
  companyName: string
  email: string
  phone: string
  mobilePhone: string
  createDate: string
  lastEdit: string
  vip: number
  /** "Prenume Nume" al celui care l-a creat; gol cand randul n-are autor scris. */
  createdBy: string
  DT_RowId?: string
}

/** Campurile formularului, aceleasi in `newContact.php` si in `editContact.php`. */
export type ContactForm = {
  id: number
  firstname: string
  lastname: string
  email: string
  phone: string
  personal_phone: string
  position: string
  credit_card_authorization_number: string
  city: string
  state: string
  zip_code: string
  address: string
  vip: number
}

/** Un rand din jurnalul contactului. `user` gol inseamna randul sistemului. */
export type ContactEvent = {
  id: number
  date: string
  /** Fara etichete HTML: serverul le scoate, fiindca textul e scris de om. */
  message: string
  userId: number
  user: string
  DT_RowId?: string
}

/** Un abonament sau un SKU, cu starea socotita pe server. */
export type ContactSubscription = {
  skuId: number
  name: string
  /** `-1` = nelimitat, `0` = fara suport, altfel numarul de zile. */
  supportLicense: number
  purchaseDate: string
  endDate: string
  status: 'active' | 'expired' | 'cancelled'
}

export type ContactSubscriptions = {
  subscriptions: ContactSubscription[]
  skus: ContactSubscription[]
}

export type ContactsTableRequest = {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
}

export function contactsTableParams(r: ContactsTableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

export const contactsShellQuery = queryOptions({
  queryKey: ['settings', 'contacts', 'shell'],
  queryFn: () => apiGet<ContactsShell>('/settings/contacts'),
})

export function contactsTableQuery(r: ContactsTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'contacts', 'list', r],
    queryFn: () => apiGet<TablePage<ContactRow>>(`/settings/contacts/list?${contactsTableParams(r).toString()}`),
  })
}

export function contactQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'contacts', 'one', id],
    queryFn: () => apiGet<{ contact: ContactForm }>(`/settings/contacts/${id}`),
    enabled: id > 0,
  })
}

/** Ce spune serverul despre scrisorile cu legatura de agent, contact cu contact. */
export type AgentLinkResult = {
  sent: number
  failed: { id: number; reason: 'not_found' | 'no_link' | 'not_sent' | 'forbidden' | string }[]
}

/** Fara `id` creeaza, cu `id` modifica - ca `add_contact` / `update_contact`. */
export function saveContact(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; id: number; agentLink?: AgentLinkResult }>('/settings/contacts', fields)
}

/**
 * "Send agent download link" - `settings/contacts/send_agent_link_by_email`,
 * cu `contacts[]` ca in `app.js`. Raspunde 200 si cu ce n-a plecat: un
 * contact din alta firma sau un server de mail cazut nu opresc lotul.
 */
export function sendAgentLink(contacts: number[]) {
  return apiPost<AgentLinkResult>('/settings/contacts/agent-link', { contacts: contacts.map(String) })
}

/** Textul de toastr pentru un raspuns, ca in `app.js` ("Email sent" / eroarea). */
export function agentLinkText(result: AgentLinkResult): { ok: boolean; text: string } {
  const reasons: Record<string, string> = {
    not_found: 'contact not found',
    no_link: 'the download server is not configured',
    not_sent: 'the email could not be sent',
    forbidden: 'not allowed',
  }
  if (result.failed.length === 0) return { ok: true, text: result.sent === 1 ? 'Email sent' : `${result.sent} emails sent` }
  const why = result.failed.map((f) => `#${f.id}: ${reasons[f.reason] ?? f.reason}`).join(', ')
  return { ok: false, text: `${result.sent} sent, ${result.failed.length} failed (${why})` }
}

export function contactEventsQuery(id: number, r: ContactsTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'contacts', 'events', id, r],
    queryFn: () =>
      apiGet<TablePage<ContactEvent>>(`/settings/contacts/${id}/events?${contactsTableParams(r).toString()}`),
    enabled: id > 0,
  })
}

export function contactSubscriptionsQuery(id: number) {
  return queryOptions({
    queryKey: ['settings', 'contacts', 'subscriptions', id],
    queryFn: () => apiGet<ContactSubscriptions>(`/settings/contacts/${id}/subscriptions`),
    enabled: id > 0,
  })
}
